import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBlogPosts } from '../services/blogService';
import { BlogPost } from '../types/blog';
import { useSeoTags, buildWebPageJsonLd } from '../hooks/useSeoTags';
import './BlogPage.css';

const BlogListPage: React.FC = () => {
  const { t } = useTranslation('blog');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedTag, setSelectedTag] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 9;

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getBlogPosts(page, limit, selectedTag);
        setPosts(response.data.posts);
        setTotal(response.data.meta.total);
      } catch (err) {
        setError(t('error.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [page, selectedTag, t]);

  // Collect all tags from posts for filter
  const allTags = Array.from(new Set(posts.flatMap(p => p.tags)));
  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const blogTitle = t('title');
  const blogSubtitle = t('subtitle');
  const seoTags = useSeoTags({
    title: blogTitle,
    description: blogSubtitle,
    canonical: '/blog',
    // Blog is a list of articles — emit a WebPage + BreadcrumbList. Individual
    // article schema (Article/BlogPosting) lives on BlogDetailPage.
    jsonLd: buildWebPageJsonLd({
      url: '/blog',
      name: blogTitle,
      description: blogSubtitle,
      breadcrumbs: [
        { name: 'Loopy', url: '/' },
        { name: blogTitle, url: '/blog' },
      ],
    }),
  });

  return (
    <div className="blog-page">
      {seoTags}
      <div className="blog-hero">
        <h1>{t('title')}</h1>
        <p>{t('subtitle')}</p>
      </div>

      <div className="blog-container">
        {/* Tag Filter */}
        {allTags.length > 0 && (
          <div className="blog-tags-filter">
            <button
              className={`blog-tag-btn ${!selectedTag ? 'active' : ''}`}
              onClick={() => { setSelectedTag(undefined); setPage(1); }}
            >
              {t('allPosts')}
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                className={`blog-tag-btn ${selectedTag === tag ? 'active' : ''}`}
                onClick={() => { setSelectedTag(tag); setPage(1); }}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="blog-loading">
            <div className="blog-spinner" />
            <p>{t('loading')}</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="blog-error">
            <p>{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && posts.length === 0 && (
          <div className="blog-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
              <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <p>{t('empty')}</p>
          </div>
        )}

        {/* Blog Grid */}
        {!loading && !error && posts.length > 0 && (
          <div className="blog-grid">
            {posts.map(post => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="blog-card"
              >
                {post.coverImageUrl && (
                  <div className="blog-card-image">
                    <img src={post.coverImageUrl} alt={post.title} />
                  </div>
                )}
                <div className="blog-card-content">
                  <div className="blog-card-meta">
                    <span className="blog-card-date">{formatDate(post.publishedAt)}</span>
                    {post.viewCount > 0 && (
                      <span className="blog-card-views">
                        {post.viewCount} {t('views')}
                      </span>
                    )}
                  </div>
                  <h2 className="blog-card-title">{post.title}</h2>
                  {post.excerpt && (
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                  )}
                  {post.tags.length > 0 && (
                    <div className="blog-card-tags">
                      {post.tags.map(tag => (
                        <span key={tag} className="blog-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="blog-pagination">
            <button
              className="blog-page-btn"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              {t('prev')}
            </button>
            <span className="blog-page-info">
              {page} / {totalPages}
            </span>
            <button
              className="blog-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              {t('next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogListPage;
